// Meetings — surfaces upcoming events from Google Calendar + past
// Fathom-ingested meetings plus their AI-generated pre-meeting briefs.
// Two-column layout (list + detail) mirrors Inbox.tsx PR #24 polish.
// List separates upcoming from past; detail pane mirrors the Lovable
// prototype's per-meeting card visual (brief in tinted box, mono time,
// attendee row, focus-block warning, inline action items).
//
// Read-only surface (N1 — agent never sends or shares). Action items are
// owner-actioned on the ActionItems page; the prototype's "Send summary
// to team" autoSend block is explicitly N1-violating and intentionally
// omitted here.
//
// SOURCE SPLIT (ADR-0013):
//   - Upcoming = `calendar_events` (Google Calendar parser, every 15s)
//     adapted to MeetingRow shape via calendarEventToMeetingRow.
//   - Past = `meetings` (Fathom parser, post-recording).
//   This is intentional: calendar = intent (schedule), meetings =
//   artifact (recording). Pre-briefs live with intent because Fathom
//   doesn't fire pre-meeting webhooks (confirmed via
//   developers.fathom.ai/webhooks). When Fathom later delivers a
//   recording, its dedup hook copies the brief onto the meetings row,
//   so past-meeting detail keeps showing it.
//
// Focus-block detection reuses the Focus Guardian agent's output: a
// meeting is "during focus block" when an `approvals(target_type=
// 'focus_reschedule', status='pending')` row exists with
// `proposed_calendar_event_id` matching the row's calendar event ID
// (which IS the row.id for upcoming, and meetings.calendar_event_id for
// past). No timezone math on the client — the workers-side Guardian
// already owns that semantic and writes the conflict surface for us to
// read.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Pencil,
  Sparkles,
  Trash2,
  Users,
  Video,
} from 'lucide-react';
import {
  isCalendarEventBriefEligible,
  isExternalAttendee,
  ownerDomainOf,
  type CalendarEventAttendee,
} from '@reverra/shared/meeting-eligibility';
import { MeetingSummaryMarkdown } from '@/components/shared/MeetingSummaryMarkdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { itemsForParticipant, ownerItemsMentioning } from '@/lib/actionItemEmail';
import { SendActionItemEmailButton } from '@/components/action-items/SendActionItemEmailButton';

type MeetingParticipant = {
  email: string | null;
  name: string | null;
  is_external: boolean;
  represents_owner: boolean;
};

type MeetingActionItem = {
  id: string;
  text: string;
  assignee_name: string | null;
  assignee_email: string | null;
  is_for_owner: boolean;
  due_date: string | null;
  status: 'open' | 'done' | 'dismissed';
};

export type MeetingRow = {
  id: string;
  // Which table this row came from. Edit/delete are offered ONLY for
  // 'meeting' (the owned Fathom artifact in `meetings`): it has a
  // meetings_owner_update RLS policy, an audit trigger (0014), and the
  // Fathom parser upserts with ignoreDuplicates so a soft-deleted row is
  // never revived. 'calendar' rows are Google Calendar's source of truth —
  // the parser re-upserts them every 15s, so they're managed in Google, not
  // here.
  source: 'meeting' | 'calendar';
  title: string | null;
  summary: string | null;
  pre_brief: string | null;
  pre_brief_generated_at: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  meeting_url: string | null;
  calendar_event_id: string | null;
  fathom_url: string | null;
  meeting_participants: MeetingParticipant[];
  meeting_action_items: MeetingActionItem[];
};

const MEETING_SELECT =
  'id, title, summary, pre_brief, pre_brief_generated_at, started_at, ended_at, duration_seconds, meeting_url, calendar_event_id, fathom_url, meeting_participants(email, name, is_external, represents_owner), meeting_action_items(id, text, assignee_name, assignee_email, is_for_owner, due_date, status)';

// Calendar-events row shape as fetched. Attendees come in already-
// normalised by the calendar parser into the shared CalendarEventAttendee
// shape — see packages/shared/src/meeting-eligibility/index.ts.
type CalendarEventRow = {
  id: string;
  title: string | null;
  start_at: string;
  end_at: string | null;
  meeting_url: string | null;
  status: string;
  is_all_day: boolean;
  attendees: CalendarEventAttendee[];
  pre_brief: string | null;
  pre_brief_generated_at: string | null;
};

const CALENDAR_EVENT_SELECT =
  'id, title, start_at, end_at, meeting_url, status, is_all_day, attendees, pre_brief, pre_brief_generated_at';

// Adapter: calendar_events row → MeetingRow shape. Keeps the rest of
// the page (list-item + detail card) unchanged — they consume a
// MeetingRow regardless of source.
//
// Mapping notes:
//   - id = calendar event id (used directly for focus-conflict matching;
//     no separate calendar_event_id column needed on the synthesized row)
//   - calendar_event_id = same id (so isFocusConflicted sees a match)
//   - meeting_participants are derived from attendees JSONB; is_external
//     is computed from the owner's email domain; represents_owner uses
//     attendee.self.
//   - summary / fathom_url / meeting_action_items are pre-meeting concepts
//     that don't exist yet → null / empty.
function calendarEventToMeetingRow(ev: CalendarEventRow, ownerDomain: string): MeetingRow {
  const durationSeconds = ev.end_at
    ? Math.max(
        0,
        Math.round((new Date(ev.end_at).getTime() - new Date(ev.start_at).getTime()) / 1000),
      )
    : null;
  return {
    id: ev.id,
    source: 'calendar',
    title: ev.title,
    summary: null,
    pre_brief: ev.pre_brief,
    pre_brief_generated_at: ev.pre_brief_generated_at,
    started_at: ev.start_at,
    ended_at: ev.end_at,
    duration_seconds: durationSeconds,
    meeting_url: ev.meeting_url,
    calendar_event_id: ev.id,
    fathom_url: null,
    meeting_participants: (ev.attendees ?? []).map((a) => ({
      email: a.email,
      name: a.name,
      is_external: isExternalAttendee(a, ownerDomain),
      represents_owner: a.self,
    })),
    meeting_action_items: [],
  };
}

async function fetchUpcoming(
  ownerId: string | null,
  ownerEmail: string | null,
): Promise<MeetingRow[]> {
  if (!ownerId) return [];
  const nowIso = new Date().toISOString();
  // 7-day window mirrors the brief agent's UPCOMING_WINDOW_DAYS. We
  // over-fetch on the SQL filter (status + !is_all_day) and let the
  // shared `isCalendarEventBriefEligible` narrow further — same
  // module the brief agent applies, so the list and the brief
  // inventory cannot drift by construction.
  const horizonIso = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from('calendar_events')
    .select(CALENDAR_EVENT_SELECT)
    // RLS scopes to owner_id = auth.uid() already; the explicit filter
    // is defense-in-depth (reads correctly without trusting RLS in the
    // application layer) and Phase-1B-ready when more than one owner
    // can co-exist on a project.
    .eq('owner_id', ownerId)
    .is('deleted_at', null)
    .eq('status', 'confirmed')
    .eq('is_all_day', false)
    .gt('start_at', nowIso)
    .lt('start_at', horizonIso)
    .order('start_at', { ascending: true })
    .limit(60);
  if (error) throw error;
  const rows = (data as unknown as CalendarEventRow[]) ?? [];
  const ownerDomain = ownerDomainOf(ownerEmail);
  return rows
    .filter((r) => isCalendarEventBriefEligible(r, ownerDomain))
    .slice(0, 20)
    .map((r) => calendarEventToMeetingRow(r, ownerDomain));
}

async function fetchPast(ownerId: string | null): Promise<MeetingRow[]> {
  if (!ownerId) return [];
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from('meetings')
    .select(MEETING_SELECT)
    // Same defense-in-depth pattern as fetchUpcoming — explicit
    // owner_id filter on top of RLS so the read path is correct
    // without trusting RLS in the application layer.
    .eq('owner_id', ownerId)
    .is('deleted_at', null)
    .lte('started_at', nowIso)
    .order('started_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  // Tag the source so the detail pane knows these are owned `meetings` rows
  // (editable/deletable) rather than synthesized calendar events.
  return ((data as unknown as Omit<MeetingRow, 'source'>[]) ?? []).map((m) => ({
    ...m,
    source: 'meeting' as const,
  }));
}

// Pending focus-reschedule proposals tell us which UPCOMING calendar
// events (and therefore meetings, via meeting.calendar_event_id) the
// Focus Guardian flagged as overlapping an active focus block. We
// only need the proposed_calendar_event_id set — the rest of the
// proposal (proposed start/end, rationale) is the Focus page's job.
async function fetchFocusConflictedEventIds(ownerId: string | null): Promise<Set<string>> {
  if (!ownerId) return new Set<string>();
  const { data, error } = await supabase
    .from('approvals')
    .select('proposed_calendar_event_id')
    // RLS scopes to owner_id = auth.uid() already; the explicit filter
    // is defense-in-depth (reads correctly without trusting RLS in the
    // application layer) and Phase-1B-ready.
    .eq('owner_id', ownerId)
    .eq('target_type', 'focus_reschedule')
    .eq('status', 'pending')
    .not('proposed_calendar_event_id', 'is', null)
    .limit(200);
  if (error) throw error;
  const ids = new Set<string>();
  for (const row of (data ?? []) as Array<{ proposed_calendar_event_id: string | null }>) {
    if (row.proposed_calendar_event_id) ids.add(row.proposed_calendar_event_id);
  }
  return ids;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

// Prototype-style time format. Today: "10:00 AM". Otherwise: short
// human-relative ("Yesterday 3:00 PM", "Tue 11:00 AM", "Apr 12, 11:00 AM").
function formatMeetingTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return format(d, 'h:mm a');
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return `Yesterday ${format(d, 'h:mm a')}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();
  if (isTomorrow) return `Tomorrow ${format(d, 'h:mm a')}`;
  // Rolling 7-day window — show short weekday name. NOT
  // calendar-week-aligned; a Friday meeting viewed on the prior
  // Friday flips to MMM d format. Acceptable for the at-a-glance use.
  const diffDays = Math.abs((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return format(d, 'EEE h:mm a');
  return format(d, 'MMM d, h:mm a');
}

const Meetings = () => {
  const { user } = useAuth();
  const ownerId = user?.id ?? null;
  const ownerEmail = user?.email ?? null;
  // Note both ownerId + ownerEmail in the queryKey so a re-login under a
  // different account refreshes the list (rare in single-tenant Phase 1A,
  // cheap insurance + correctness for Phase 1B handover).
  const upcomingQ = useQuery({
    queryKey: ['meetings', 'upcoming', ownerId, ownerEmail],
    queryFn: () => fetchUpcoming(ownerId, ownerEmail),
    enabled: ownerId !== null,
  });
  const pastQ = useQuery({
    queryKey: ['meetings', 'past', ownerId],
    queryFn: () => fetchPast(ownerId),
    enabled: ownerId !== null,
  });
  // refetchInterval keeps the "During focus block" warning in sync
  // with the Focus page — when the owner approves a focus_reschedule
  // there, this query refetches within 30s and the amber ring on the
  // affected meeting clears. Without this, the warning persists for
  // the full React Query staleTime (5 min by default) which produces
  // a confusing "still flagged" state immediately after approval.
  const focusConflictsQ = useQuery({
    queryKey: ['meetings', 'focus-conflicts', ownerId],
    queryFn: () => fetchFocusConflictedEventIds(ownerId),
    refetchInterval: 30_000,
    enabled: ownerId !== null,
  });

  // Selected meeting (left list → right detail). null on empty list.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Combined list (upcoming first, then past) for selection logic.
  const allMeetings = useMemo(() => {
    return [...(upcomingQ.data ?? []), ...(pastQ.data ?? [])];
  }, [upcomingQ.data, pastQ.data]);

  // Auto-select the first row when data arrives or selection becomes
  // stale. Functional updater so the effect doesn't depend on
  // selectedId (avoids redundant invocations).
  useEffect(() => {
    if (!upcomingQ.data || !pastQ.data) return;
    const ids = allMeetings.map((r) => r.id);
    setSelectedId((curr) => (curr && ids.includes(curr) ? curr : (ids[0] ?? null)));
  }, [allMeetings, upcomingQ.data, pastQ.data]);

  const selected = useMemo(
    () => allMeetings.find((m) => m.id === selectedId) ?? null,
    [allMeetings, selectedId],
  );

  const upcomingCount = upcomingQ.data?.length ?? 0;
  const pastCount = pastQ.data?.length ?? 0;
  const totalCount = upcomingCount + pastCount;

  const conflictedEventIds = focusConflictsQ.data ?? new Set<string>();
  const isFocusConflicted = (row: MeetingRow) =>
    !!row.calendar_event_id && conflictedEventIds.has(row.calendar_event_id);

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Meetings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {totalCount === 0
              ? 'No meetings yet. New Fathom recordings surface here within ~60s of upload.'
              : 'AI-prepared briefs and post-meeting summaries. Owner-only — never sent or shared (N1).'}
          </p>
        </div>
      </motion.header>

      <section>
        {(upcomingQ.isLoading || pastQ.isLoading) && (
          <p className="text-sm text-muted-foreground">Loading meetings…</p>
        )}
        {(upcomingQ.isError || pastQ.isError) && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">
              Failed to load meetings:{' '}
              {((upcomingQ.error ?? pastQ.error) as Error | undefined)?.message}
            </p>
          </GlassCard>
        )}

        {allMeetings.length === 0 && !upcomingQ.isLoading && !pastQ.isLoading && (
          <GlassCard hover={false} variant="inset">
            <p className="text-sm text-muted-foreground">
              No meetings yet. Fathom recordings will surface here within ~60s of upload.
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground/80">
              Pre-meeting briefs are agent-generated for upcoming meetings (within 7 days). Briefs
              are owner-only — never sent or shared (N1).
            </p>
          </GlassCard>
        )}

        {allMeetings.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:h-[calc(100vh-14rem)] lg:min-h-[560px]">
            {/* List column */}
            <div className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:pr-2">
              {upcomingCount > 0 && (
                <>
                  <h2 className="text-sm font-semibold text-foreground mt-1">
                    Today &amp; Upcoming
                    <span className="ml-2 text-[11px] font-mono text-muted-foreground font-normal">
                      {upcomingCount}
                    </span>
                  </h2>
                  <div className="space-y-3">
                    <AnimatePresence mode="popLayout" initial={false}>
                      {(upcomingQ.data ?? []).map((m) => (
                        <motion.div
                          key={m.id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          <MeetingListItem
                            row={m}
                            selected={m.id === selectedId}
                            isUpcoming
                            focusConflict={isFocusConflicted(m)}
                            onSelect={() => setSelectedId(m.id)}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </>
              )}
              {pastCount > 0 && (
                <>
                  <h2 className="text-sm font-semibold text-foreground mt-4">
                    Past Meetings
                    <span className="ml-2 text-[11px] font-mono text-muted-foreground font-normal">
                      {pastCount}
                    </span>
                  </h2>
                  <div className="space-y-3">
                    {(pastQ.data ?? []).map((m) => (
                      <MeetingListItem
                        key={m.id}
                        row={m}
                        selected={m.id === selectedId}
                        isUpcoming={false}
                        // Past meetings can't conflict with a future
                        // focus block — Guardian only proposes reschedules
                        // for upcoming events. Hardcoded false on purpose;
                        // skipping the lookup avoids dead UI noise.
                        focusConflict={false}
                        onSelect={() => setSelectedId(m.id)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Detail column — `min-w-0` is mandatory so the grid column
                can shrink below its intrinsic content width. Without it,
                long unbreakable tokens (URLs in `<a>` href, email in
                `<...>` brackets, long words in markdown) blow the column
                out past the card border at narrow viewports. */}
            <div className="lg:col-span-3 lg:h-full lg:overflow-y-auto min-w-0">
              {selected ? (
                <MeetingDetail
                  row={selected}
                  focusConflict={isFocusConflicted(selected)}
                  onDeleted={() => setSelectedId(null)}
                />
              ) : (
                <GlassCard hover={false} variant="inset" className="h-full min-h-[400px]">
                  <p className="text-sm text-muted-foreground">Select a meeting to view.</p>
                </GlassCard>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

type MeetingListItemProps = {
  row: MeetingRow;
  selected: boolean;
  isUpcoming: boolean;
  focusConflict: boolean;
  onSelect: () => void;
};

function MeetingListItem({
  row,
  selected,
  isUpcoming,
  focusConflict,
  onSelect,
}: MeetingListItemProps) {
  const briefReady = !!row.pre_brief && isUpcoming;
  const openActionItems = row.meeting_action_items.filter((a) => a.status === 'open').length;
  const TimeIcon = isUpcoming ? Calendar : Clock;
  return (
    <GlassCard
      hover={false}
      ariaPressed={selected}
      className={`cursor-pointer transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : focusConflict
            ? 'ring-1 ring-attention/20 hover:ring-attention/40'
            : ''
      }`}
      onClick={onSelect}
    >
      {/* Time row — mono, prototype-style */}
      <div className="flex items-center gap-2 mb-1">
        <TimeIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-mono text-muted-foreground">
          {formatMeetingTime(row.started_at)}
        </span>
        {focusConflict && (
          <span
            className="ml-auto flex items-center gap-1 text-[10px] text-attention shrink-0"
            title="Overlaps an active focus block — see the Focus page"
          >
            <AlertTriangle className="w-3 h-3" /> focus
          </span>
        )}
        {!focusConflict && briefReady && (
          <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-forest-soft text-forest shrink-0">
            <Sparkles className="w-3 h-3" /> brief
          </span>
        )}
        {!focusConflict && !briefReady && isUpcoming && row.meeting_participants.length > 0 && (
          <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-secondary text-muted-foreground shrink-0">
            <PulseDot color="amber" /> briefing
          </span>
        )}
      </div>
      {/* Title */}
      <div className="flex items-start gap-2">
        <Video className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <h3 className="text-sm font-semibold text-foreground leading-snug break-words">
          {row.title ?? '(no title)'}
        </h3>
      </div>
      {/* Footer meta */}
      <div className="mt-2 flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
        <span>{formatDuration(row.duration_seconds)}</span>
        {row.meeting_participants.length > 0 && (
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {row.meeting_participants.length}
          </span>
        )}
        {openActionItems > 0 && (
          <Badge variant="secondary" className="ml-auto">
            {openActionItems} open
          </Badge>
        )}
      </div>
    </GlassCard>
  );
}

type MeetingDetailProps = {
  row: MeetingRow;
  focusConflict: boolean;
  onDeleted: () => void;
};

export function MeetingDetail({ row, focusConflict, onDeleted }: MeetingDetailProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const ownerId = user?.id ?? null;
  const ownerEmail = user?.email ?? null;
  const [briefExpanded, setBriefExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({ title: row.title ?? '', summary: row.summary ?? '' });
  // Edit/delete only for owned `meetings` rows (see MeetingRow.source). Calendar
  // events are managed in Google.
  const canManage = row.source === 'meeting';

  // Re-seed the form and drop any open edit/confirm state when the selected
  // meeting changes. Keyed on row.id ONLY (intentionally not row.title/summary):
  // depending on those would clobber an in-progress edit if a background refetch
  // (e.g. an action-item toggle invalidating ['meetings','past']) updated the
  // row mid-edit. (exhaustive-deps is not enabled in this package's eslint.)
  useEffect(() => {
    setEditing(false);
    setConfirmDelete(false);
    setForm({ title: row.title ?? '', summary: row.summary ?? '' });
  }, [row.id]);

  // Refresh only the past-meetings list (the editable source). The broad
  // ['meetings'] prefix would also invalidate the 30s focus-conflicts poll.
  const invalidatePast = () =>
    qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'meetings' && q.queryKey[1] === 'past',
    });

  const saveEdit = useMutation({
    mutationFn: async () => {
      const title = form.title.trim();
      if (!title) throw new Error('Title cannot be empty.');
      const { error } = await supabase
        .from('meetings')
        .update({ title, summary: form.summary.trim() || null })
        // owner_id is defence-in-depth on top of meetings_owner_update RLS —
        // matches every other write in this file.
        .eq('id', row.id)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onSuccess: () => {
      const title = form.title.trim();
      const summary = form.summary.trim() || null;
      // Patch the cached past-list row so the read view shows the new title +
      // summary immediately, instead of flashing the stale cached values until
      // invalidatePast()'s refetch settles.
      qc.setQueriesData<MeetingRow[]>(
        { predicate: (q) => q.queryKey[0] === 'meetings' && q.queryKey[1] === 'past' },
        (old) => (old ?? []).map((m) => (m.id === row.id ? { ...m, title, summary } : m)),
      );
      setEditing(false);
      invalidatePast();
      toast.success('Meeting updated.');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save changes.'),
  });

  const del = useMutation({
    mutationFn: async () => {
      // Soft-delete (§8: meetings are Fathom artifacts with source provenance +
      // an audit trigger — never hard-deleted). The Fathom parser upserts with
      // ignoreDuplicates, so a soft-deleted meeting is NOT revived on re-ingest.
      // The list queries already filter `deleted_at IS NULL`, so it drops out.
      const { error } = await supabase
        .from('meetings')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', row.id)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidatePast();
      toast.success('Meeting deleted.');
      // Clear selection — the parent re-picks the first remaining row.
      onDeleted();
    },
    onError: (err) => {
      setConfirmDelete(false);
      toast.error(err instanceof Error ? err.message : 'Could not delete meeting.');
    },
  });

  const isUpcoming = new Date(row.started_at).getTime() > Date.now();
  const briefReady = !!row.pre_brief && isUpcoming;
  const attendeeLabels = row.meeting_participants
    .map((p) => p.name ?? p.email ?? '(unknown)')
    .join(', ');

  return (
    <GlassCard
      hover={false}
      className={`flex flex-col min-w-0 ${focusConflict ? 'ring-1 ring-attention/20' : ''}`}
    >
      {confirmDelete && (
        <div className="mb-4 -mx-4 -mt-4 px-4 py-3 bg-alert/5 border-b border-alert/20 flex items-center justify-between gap-3 rounded-t-[inherit]">
          <span className="text-sm text-foreground">Remove this meeting from your deck?</span>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={del.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="!bg-alert hover:!bg-alert/90"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      )}
      {/* Header — mono time + focus warning + title + brief status pill */}
      <div className="pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {isUpcoming ? (
            <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
          ) : (
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
          <span className="text-sm font-mono text-muted-foreground">
            {formatMeetingTime(row.started_at)}
          </span>
          {focusConflict && (
            <span className="text-xs text-attention flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> During focus block
            </span>
          )}
          <span className="ml-auto flex items-center gap-2 flex-wrap">
            {canManage && !editing && (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  aria-label="Edit meeting"
                  title="Edit"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-forest hover:bg-secondary transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  aria-label="Delete meeting"
                  title="Delete"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-alert hover:bg-alert/5 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </span>
            )}
            {row.calendar_event_id && (
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-sage/10 text-sage shrink-0">
                calendar linked
              </span>
            )}
            {row.fathom_url && (
              <a
                href={row.fathom_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-learning/10 text-learning hover:opacity-80 transition-opacity shrink-0"
              >
                fathom ↗
              </a>
            )}
          </span>
        </div>
        {editing ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Title</span>
              <input
                className="mt-1 w-full rounded-md neo-inset px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest/40"
                value={form.title}
                maxLength={300}
                autoFocus
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Summary</span>
              <textarea
                className="mt-1 w-full min-h-[140px] resize-y rounded-md neo-inset px-3 py-2 text-sm text-foreground leading-relaxed focus:outline-none focus:ring-2 focus:ring-forest/40"
                placeholder="No summary yet — add notes here."
                value={form.summary}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              />
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => saveEdit.mutate()}
                disabled={saveEdit.isPending || !form.title.trim()}
              >
                {saveEdit.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setForm({ title: row.title ?? '', summary: row.summary ?? '' });
                }}
                disabled={saveEdit.isPending}
              >
                Cancel
              </Button>
            </div>
            <p className="text-[11px] font-mono text-muted-foreground">
              {new Date(row.started_at).toLocaleString()} · {formatDuration(row.duration_seconds)}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-foreground leading-snug break-words">
                {row.title ?? '(no title)'}
              </h3>
              {briefReady && (
                <span className="flex items-center gap-1.5 text-xs text-sage shrink-0 mt-0.5">
                  <PulseDot color="green" />
                  Brief ready
                </span>
              )}
            </div>
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              {new Date(row.started_at).toLocaleString()} · {formatDuration(row.duration_seconds)}
            </p>
          </>
        )}
      </div>

      {/* Attendees row (prototype-style: Users icon + comma list) */}
      {row.meeting_participants.length > 0 && (
        <div className="py-3 border-b border-border flex items-start gap-2">
          <Users className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground break-words min-w-0">{attendeeLabels}</p>
        </div>
      )}

      {/* Pre-meeting brief — prototype-style tinted box */}
      {row.pre_brief && (
        <div className="py-4 border-b border-border">
          <button
            onClick={() => setBriefExpanded((v) => !v)}
            className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-forest hover:opacity-80 transition-opacity"
            aria-expanded={briefExpanded}
          >
            {briefExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            <Sparkles className="w-3 h-3" />
            Pre-meeting brief
            {row.pre_brief_generated_at && (
              <span className="text-muted-foreground/80 ml-1 normal-case tracking-normal">
                · generated{' '}
                {formatDistanceToNow(new Date(row.pre_brief_generated_at), { addSuffix: true })}
              </span>
            )}
          </button>
          {briefExpanded && (
            <div className="mt-2 neo-inset p-3">
              {/* Brief is plain-text with UPPERCASE section labels (see
                  agents/meeting/prompts/brief.ts) — render with
                  whitespace-pre-wrap so blank-line section breaks are
                  preserved. `break-words` keeps long unbreakable tokens
                  inside the box at narrow viewports. Recessed neo-inset
                  well (matches Inbox + Agreements detail panes) avoids the
                  card-in-card seam that the prior bg-secondary +
                  rounded-lg created against the outer GlassCard. */}
              <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed font-sans">
                {row.pre_brief}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary (post-meeting) — Fathom delivers
          `default_summary.markdown_formatted` as proper Markdown.
          MeetingSummaryMarkdown handles the project-token rendering.
          Recessed neo-inset well matches the pre-meeting brief + the Inbox
          detail pane; avoids the card-in-card seam against the outer
          GlassCard background. */}
      {!editing && row.summary && (
        <div className="py-4 border-b border-border">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Summary</p>
          <div className="neo-inset p-3">
            <MeetingSummaryMarkdown markdown={row.summary} />
          </div>
        </div>
      )}

      {/* Participants detail (richer than the comma row above — external/you
          badges, email tooltip). Each participant with OPEN action items gets a
          follow-up email button: one email to THEM covering all their items
          ("Hey Mani, you mentioned you'd… — what's the status?"). The owner's
          own row gets a self-reminder button for his "→ you" items (it arrives
          from reverra@agentmail.to, so emailing yourself is fine). */}
      {row.meeting_participants.length > 0 && (
        <div className="py-4 border-b border-border">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
            Participants
          </p>
          <ul className="space-y-1">
            {row.meeting_participants.map((p, i) => {
              // EVERY participant with an address gets an email button: their
              // own open items (status-check), plus the owner's items that
              // mention them by name ("Email Priscilla the labels" is Joel's
              // item — Priscilla's row is the natural click target), plus a
              // plain meeting follow-up when there are no items at all.
              const theirItems =
                row.source === 'meeting' && ownerId
                  ? itemsForParticipant(p, row.meeting_action_items)
                  : [];
              const aboutThem =
                row.source === 'meeting' && ownerId
                  ? ownerItemsMentioning(p, row.meeting_action_items)
                  : [];
              const coveredIds = [...theirItems, ...aboutThem].map((a) => a.id);
              const recipientEmail = p.represents_owner ? (p.email ?? ownerEmail) : p.email;
              // Every participant with an address gets the button; the owner's
              // OWN row only when he has "→ you" items — a zero-item
              // self-reminder has no anchor to carry the is_for_owner ground
              // truth, so the worker couldn't reliably keep the reminder
              // framing (the owner-email identities aren't reconciled).
              const showEmailButton = Boolean(
                ownerId &&
                recipientEmail &&
                row.source === 'meeting' &&
                (!p.represents_owner || theirItems.length > 0),
              );
              return (
                <li
                  key={`${p.email ?? 'no-email'}-${i}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm min-w-0"
                >
                  <span className="text-foreground break-words">
                    {p.name ?? p.email ?? '(unknown)'}
                  </span>
                  {p.email && p.name && (
                    <span className="text-xs text-muted-foreground break-all">
                      &lt;{p.email}&gt;
                    </span>
                  )}
                  {p.is_external && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded neo-chip bg-attention/10 text-attention shrink-0">
                      external
                    </span>
                  )}
                  {p.represents_owner && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded neo-chip bg-forest-soft text-forest shrink-0">
                      you
                    </span>
                  )}
                  {showEmailButton && (
                    <span className="ml-auto shrink-0 flex items-center gap-1">
                      {coveredIds.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {coveredIds.length} open item{coveredIds.length > 1 ? 's' : ''}
                        </span>
                      )}
                      <SendActionItemEmailButton
                        actionItemId={theirItems[0]?.id ?? null}
                        coveredItemIds={coveredIds}
                        sourceMeetingId={row.id}
                        candidates={[
                          { email: recipientEmail!, name: p.represents_owner ? null : p.name },
                        ]}
                        ownerId={ownerId!}
                        selfReminder={p.represents_owner}
                        compact
                      />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Action items — an interactive checklist. Ticking a box toggles the
          item's status (open ↔ done) and persists via the meeting_action_items
          owner_update RLS policy. N1-safe: this only marks your own items;
          it never sends/shares anything. */}
      <ActionItemChecklist items={row.meeting_action_items} />

      <p className="mt-auto pt-3 text-[11px] text-muted-foreground/80">
        Brief and summary are owner-only — never sent to attendees or shared (N1).
      </p>
    </GlassCard>
  );
}

// Interactive action-items checklist for a meeting. Each row is a real
// checkbox: clicking toggles open ↔ done and writes meeting_action_items.status
// (owner_update RLS). Optimistic local overrides give instant feedback; the
// ['meetings'] queries are invalidated on success so the persisted state wins.
function ActionItemChecklist({ items }: { items: MeetingActionItem[] }): JSX.Element | null {
  const qc = useQueryClient();
  const [overrides, setOverrides] = useState<Record<string, 'open' | 'done'>>({});

  const toggle = useMutation({
    mutationFn: async (args: { id: string; status: 'open' | 'done' }) => {
      const { error } = await supabase
        .from('meeting_action_items')
        .update({ status: args.status })
        .eq('id', args.id);
      if (error) throw error;
    },
    onError: (_err, args) => {
      setOverrides((o) => {
        const next = { ...o };
        delete next[args.id];
        return next;
      });
      toast.error('Could not update that action item.');
    },
    onSuccess: () =>
      // Refresh only the action-item-bearing lists. The broad ['meetings']
      // prefix would also invalidate ['meetings','focus-conflicts',…], which
      // polls the approvals table on a 30s interval — invalidating it on every
      // checkbox click triggers a spurious approvals refetch (reviewer R-fe M2).
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'meetings' &&
          (q.queryKey[1] === 'upcoming' || q.queryKey[1] === 'past'),
      }),
  });

  // Dismissed items are hidden; open + done render as the checklist.
  const visible = items.filter((a) => a.status !== 'dismissed');
  if (visible.length === 0) return null;

  const effectiveDone = (a: MeetingActionItem): boolean =>
    (overrides[a.id] ?? (a.status === 'done' ? 'done' : 'open')) === 'done';

  const remaining = visible.filter((a) => !effectiveDone(a)).length;

  return (
    <div className="py-4 flex-1">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
        Action items{visible.length > 0 && ` · ${remaining} of ${visible.length} open`}
      </p>
      <ul className="space-y-1.5">
        {visible.map((a) => {
          const done = effectiveDone(a);
          return (
            <li key={a.id} className="flex items-start gap-2 text-sm min-w-0">
              <button
                type="button"
                role="checkbox"
                aria-checked={done}
                aria-label={done ? `Mark "${a.text}" not done` : `Mark "${a.text}" done`}
                onClick={() => {
                  const nextStatus = done ? 'open' : 'done';
                  setOverrides((o) => ({ ...o, [a.id]: nextStatus }));
                  toggle.mutate({ id: a.id, status: nextStatus });
                }}
                className="shrink-0 mt-0.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40"
              >
                {done ? (
                  <CheckCircle2 className="w-4 h-4 text-forest" aria-hidden="true" />
                ) : (
                  <Circle
                    className="w-4 h-4 text-sage hover:text-forest transition-colors"
                    aria-hidden="true"
                  />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <p
                  className={`break-words ${done ? 'text-foreground/50 line-through' : 'text-foreground'}`}
                >
                  {a.text}
                </p>
                {!done && (
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5 break-words">
                    {a.is_for_owner
                      ? '→ you'
                      : `→ ${a.assignee_name ?? a.assignee_email ?? 'unassigned'}`}
                    {a.due_date && ` · due ${a.due_date}`}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default Meetings;
